import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Adds the free-shipping offer variant to the cart without leaving the page.
 * Follows the same request and event contract as product-form.js so every
 * cart-items-component re-renders from the returned sections. Without
 * JavaScript, the wrapped form still posts to /cart/add.
 */
class FreeShippingUpsell extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    this.addEventListener('submit', this.#onSubmit, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * @param {SubmitEvent} event
   */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');

    const formData = new FormData(form);
    const variantId = String(formData.get('id') ?? '');
    const quantity = Number(formData.get('quantity') ?? 1);
    if (!variantId) return;

    const sectionIds = [];
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.push(component.dataset.sectionId);
      }
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity }],
          sections: sectionIds.join(','),
        }),
      });
      const result = await addResponse.json();
      const cart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
      }).then((response) => response.json());

      if (result.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'free-shipping-upsell', itemCount: quantity },
        });
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'free-shipping-upsell',
          itemCount: quantity,
          sections: result.sections,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      button?.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('free-shipping-upsell')) {
  customElements.define('free-shipping-upsell', FreeShippingUpsell);
}
